import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { flushSync } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { useOutletContext } from "react-router";
import { ArrowLeft, FileText, History as HistoryIcon, Inbox, Menu, Plus, RotateCw, Route } from "lucide-react";
import type { CockpitContext } from "@/app/routes/dashboard/CockpitLayout";
import { usePhoneLaneSlotRef } from "@/app/routes/dashboard/agent/phoneLaneSlot";
import { queue } from "@/components/Toast";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitTabIndicator } from "@/app/routes/dashboard/components/kit/TabIndicator";
import { agentRail } from "@/app/routes/dashboard/agent/agentFocusStore";
import { EmptyState, ErrorState, StateAction } from "@/app/routes/dashboard/components/ViewStates";
import type { MarketSignal } from "@/types/marketSignal.type";
import type { FieldReport } from "@/types/fieldReport.type";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/utils/helpers";
import type { CreateSignalVars } from "./api/useMarketSignals";
import {
  WallBar,
  WallSaveButton,
  WallViewHeader,
} from "@/app/routes/dashboard/components/WallBar";
import { calendarDaysUntil, computeHorizon, formatShortDate } from "./components/signalMeta";
import PendingSignalCard, { type PendingSignalEdit } from "./components/PendingSignalCard";
import CreateSignalForm from "./components/CreateSignalForm";
import SteeringSheet from "./components/SteeringSheet";
import HistoryTrail from "./components/HistoryTrail";
import ReportsTrail from "./components/ReportsTrail";
import SignalsDialog from "./components/SignalsDialog";
import SignalSourcesModal from "./components/SignalSourcesModal";
import SourceSheet from "./components/SourceSheet";
import TriageDeck from "./components/TriageDeck";
import {
  DeckSkeleton,
  HistorySkeleton,
  ReportsSkeleton,
  SteeringSkeleton,
  WaitingSkeleton,
} from "./components/SignalsSkeletons";
import { HOUSE_EASE, HOUSE_TRANSITION, prefersReducedMotion } from "./components/signalsMotion";
import "./signals.css";

/* Tab order for the tablist. Drives roving-tabindex arrow-key nav and which panel
   mounts; SignalsTab is derived from it so the two never drift out of sync. */
const TAB_ORDER = ["waiting", "steering", "reports", "history"] as const;
type SignalsTab = (typeof TAB_ORDER)[number];

const TAB_LABELS: Record<SignalsTab, string> = {
  waiting: "Waiting",
  steering: "Steering",
  reports: "Reports",
  history: "History",
};

/** Outlives the triage exit, so the direction a card leaves in is still known
 *  for as long as it is animating out. */
const PENDING_EXIT_MS = 450;
const EXIT_DISTANCE = 48;
/** How long a steering dismissal is held for Undo before it is sent. */
const DISMISS_HOLD_MS = 5000;

const COUNT_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
/** "Five", for a count that opens a sentence; numerals past ten. */
function countWord(n: number): string {
  return COUNT_WORDS[n] ?? String(n);
}

/** How the board talks back to data. Real mutations in prod; local simulation in the playground. */
export interface SignalsActions {
  /** Optimistic: the caller closes the form on confirm. */
  create: (draft: CreateSignalVars) => void;
  /** `quiet`: the caller shows the outcome itself (the phone deck's toast). */
  accept: (
    signal: MarketSignal,
    validUntil: string | null,
    cbs: { onError: () => void; onSuccess: () => void },
    quiet?: boolean,
  ) => void;
  decline: (
    signal: MarketSignal,
    cbs: { onError: () => void; onSuccess: () => void },
    quiet?: boolean,
  ) => void;
  editPending: (signal: MarketSignal, patch: PendingSignalEdit) => void;
  extend: (signal: MarketSignal, validUntil: string | null) => void;
  dismissActive: (signal: MarketSignal, cbs?: { onError: () => void }) => void;
  graduate: (signal: MarketSignal) => void;
  isBusy: (id: string) => boolean;
}

/**
 * The view's one-line subhead: what is waiting on a person, what is steering,
 * and how soon the next window closes. Figures are set in ink; the rest reads
 * as the muted line it sits in.
 */
function StatusLine({
  loading,
  failed,
  waiting,
  steering,
  nextDays,
}: {
  loading: boolean;
  /** The read failed: no counts, never zeros. */
  failed: boolean;
  waiting: number;
  steering: number;
  /** Calendar days until the soonest window closes, or null when none is windowed. */
  nextDays: number | null;
}) {
  if (failed) return <>Couldn't read this brand's signals.</>;
  if (loading) return <>Reading this brand's signals.</>;
  return (
    <>
      {waiting === 0 ? (
        <>
          <strong>Nothing waiting</strong> for your call.
        </>
      ) : (
        <>
          <strong>{waiting}</strong> waiting for your call.
        </>
      )}{" "}
      {steering === 0 ? (
        <>
          <strong>Nothing steering</strong> the work yet.
        </>
      ) : (
        <>
          <strong>{steering}</strong> steering the work
          {nextDays == null ? (
            "."
          ) : nextDays === 0 ? (
            <>
              ; the next window closes <strong>today</strong>.
            </>
          ) : (
            <>
              ; the next window closes in <strong>{nextDays}</strong>{" "}
              {nextDays === 1 ? "day" : "days"}.
            </>
          )}
        </>
      )}
    </>
  );
}

/**
 * SignalsBoard: the whole Signals view (Waiting / Steering / Reports / History),
 * presentation only. SignalsSurface wires it to live data; the dev-only
 * playground feeds it fixtures. All triage state (departing cards, busy guards,
 * focus hand-off) lives here so both mounts behave identically.
 *
 * It uses the wall's own header and bar, so it has exactly the Trends and
 * References geometry: one left edge for the title, the first tab and every
 * panel below them.
 *
 * Below 768px the Waiting tab is the triage deck (TriageDeck) instead of the
 * card list: one card at a time, swiped or decided with the square buttons,
 * each decision held for five seconds of Undo before it is sent. Desktop keeps
 * the cards, and a click there sends at once.
 */
export default function SignalsBoard({
  signals,
  reports,
  isLoading,
  isError = false,
  onRetry,
  reportsLoading = false,
  viewerName = "",
  canEdit,
  memberNames,
  actions,
}: {
  signals: MarketSignal[];
  reports: FieldReport[];
  isLoading: boolean;
  /** The signals read failed: the page says so instead of showing empty tabs. */
  isError?: boolean;
  onRetry?: () => void;
  /** The reports query has not answered yet: its tab prints no count and its panel a skeleton. */
  reportsLoading?: boolean;
  /** The signed-in person, for the phone deck's greeting. */
  viewerName?: string;
  canEdit: boolean;
  memberNames: Map<string, string>;
  actions: SignalsActions;
}) {
  const reduced = prefersReducedMotion();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const cockpit = useOutletContext<CockpitContext | undefined>();
  const laneSlotRef = usePhoneLaneSlotRef();

  const [showCreate, setShowCreate] = useState(false);
  /** The desktop Sources dialog, opened on one source when a row was clicked. */
  const [sourcesFor, setSourcesFor] = useState<{ signal: MarketSignal; focusId?: string } | null>(
    null,
  );
  /** The phone's source sheet: one of the deck card's sources. */
  const [sourceSheet, setSourceSheet] = useState<{ signal: MarketSignal; index: number } | null>(
    null,
  );
  /** Sources opened in the sheet this visit, marked "Opened" on the deck. */
  const [openedSources, setOpenedSources] = useState<Set<string>>(() => new Set());
  const [activeTab, setActiveTab] = useState<SignalsTab>("waiting");
  /** A report link asked History to open on one signal; seq remounts the trail to ask again. */
  const [historyFocus, setHistoryFocus] = useState<{ id: string; seq: number } | null>(null);
  /** A signal's "From the field report" link opened Reports on that report and its
   *  filed entry; seq remounts the trail to ask again. */
  const [reportsFocus, setReportsFocus] = useState<{
    reportId: string;
    signalId: string;
    seq: number;
  } | null>(null);
  /** The steering runways have drawn in once this visit and will not again. */
  const [runwaysDrawn, setRunwaysDrawn] = useState(false);
  /** Signals the user accepted/declined: out of the queue at once, back only on error. */
  const [departedIds, setDepartedIds] = useState<Set<string>>(() => new Set());
  /** The direction each triaged card leaves in, for as long as it is animating out. */
  const [departingDirs, setDepartingDirs] = useState<Map<string, "accept" | "decline">>(
    () => new Map(),
  );
  const queueRef = useRef<HTMLDivElement>(null);
  /** Counts report-link visits, so each one remounts the trail on its report. */
  const reportsFocusSeq = useRef(0);

  // Lifecycle buckets (by status string).
  const pending = useMemo(() => signals.filter((s) => s.status === "pending"), [signals]);
  /** Steering signals dismissed and held for Undo (or sent and not yet gone). */
  const [heldDismissals, setHeldDismissals] = useState<Set<string>>(() => new Set());
  const active = useMemo(
    () => signals.filter((s) => s.status === "active" && !heldDismissals.has(s.id)),
    [signals, heldDismissals],
  );
  const history = useMemo(
    () =>
      signals.filter((s) =>
        ["expired", "graduated", "superseded", "dismissed"].includes(s.status),
      ),
    [signals],
  );

  // Presentation-only ordering: highest confidence first, minus what was just triaged.
  const visiblePending = useMemo(
    () =>
      pending
        .filter((s) => !departedIds.has(s.id))
        .sort((a, b) => (b.confidence ?? -1) - (a.confidence ?? -1)),
    [pending, departedIds],
  );

  // The soonest close among active, windowed, still-running signals, in calendar days.
  const nextWindowDays = useMemo(() => {
    let min: number | null = null;
    for (const s of active) {
      if (!s.validUntil || computeHorizon(s).expired) continue;
      const days = Math.max(0, calendarDaysUntil(s.validUntil));
      if (min == null || days < min) min = days;
    }
    return min;
  }, [active]);

  // "Last accepted by": the newest acceptance whose curator the org still knows.
  const lastAccepted = useMemo(() => {
    let best: { name: string; at: string } | null = null;
    for (const s of signals) {
      if (!s.acceptedAt || !s.acceptedByUserId) continue;
      const name = memberNames.get(s.acceptedByUserId);
      if (!name) continue;
      if (!best || new Date(s.acceptedAt).getTime() > new Date(best.at).getTime()) {
        best = { name, at: s.acceptedAt };
      }
    }
    return best;
  }, [signals, memberNames]);

  /* ── Triage ─────────────────────────────────────────────────────────────
     The card leaves at once and only that card comes back on error. */

  const abortDeparting = (signalId: string) => {
    setDepartingDirs((prev) => {
      if (!prev.has(signalId)) return prev;
      const next = new Map(prev);
      next.delete(signalId);
      return next;
    });
    setDepartedIds((prev) => {
      if (!prev.has(signalId)) return prev;
      const next = new Set(prev);
      next.delete(signalId);
      return next;
    });
  };

  const finishDeparting = (signalId: string) => {
    setDepartingDirs((prev) => {
      if (!prev.has(signalId)) return prev;
      const next = new Map(prev);
      next.delete(signalId);
      return next;
    });
  };

  const releaseDeparted = (signalId: string) => {
    setDepartedIds((prev) => {
      if (!prev.has(signalId)) return prev;
      const next = new Set(prev);
      next.delete(signalId);
      return next;
    });
  };

  const exitPending = (signal: MarketSignal, dir: "accept" | "decline", run: () => void) => {
    const index = visiblePending.findIndex((s) => s.id === signal.id);
    const item = document.getElementById(`sig-${signal.id}`);
    const hadFocus = !!item && item.contains(document.activeElement);

    // Two commits, in this order, both before the mutation's optimistic cache
    // patch: the card first renders as leaving (inert, carrying its exit
    // direction), then leaves the list, so AnimatePresence plays exactly the
    // exit that last render described.
    flushSync(() => setDepartingDirs((prev) => new Map(prev).set(signal.id, dir)));
    flushSync(() => setDepartedIds((prev) => new Set(prev).add(signal.id)));

    // Keyboard hand-off: whoever triaged from the keyboard lands on the card that
    // slides into the gap, never on <body>.
    if (hadFocus) {
      const remaining = Array.from(
        queueRef.current?.querySelectorAll<HTMLElement>("[data-sig-item]:not([data-leaving])") ??
          [],
      );
      const target = remaining[index] ?? remaining[index - 1] ?? null;
      (target?.querySelector<HTMLElement>("article") ??
        document.getElementById("sig-panel-waiting"))?.focus({ preventScroll: true });
    }

    run();
    window.setTimeout(() => finishDeparting(signal.id), PENDING_EXIT_MS);
  };

  /* The phone deck's decisions: held (out of the queue, nothing sent), undone
     (back in, nothing sent), then sent with the same narrow rollback as a click. */
  const [deckFailure, setDeckFailure] = useState<{
    signal: MarketSignal;
    dir: "accept" | "decline";
  } | null>(null);

  const holdDecision = (signal: MarketSignal) =>
    setDepartedIds((prev) => new Set(prev).add(signal.id));

  const sendDecision = (
    signal: MarketSignal,
    dir: "accept" | "decline",
    validUntil: string | null,
    permanent: boolean,
  ) => {
    const cbs = {
      onError: () => {
        abortDeparting(signal.id);
        setDeckFailure({ signal, dir });
      },
      onSuccess: () => {
        releaseDeparted(signal.id);
        // Permanent: accept, then graduate into Brand DNA.
        if (permanent) actions.graduate(signal);
      },
    };
    // Quiet: the deck says what happened in its own toast.
    if (dir === "accept") actions.accept(signal, validUntil, cbs, true);
    else actions.decline(signal, cbs, true);
  };

  /* ── Dismissing a steering signal ─────────────────────────────────────────
     Dismiss cannot be taken back by the API, so by the house rule (confirm if
     permanent, undo if not) it holds for five seconds with Undo, as the phone
     deck's decisions do, then sends. Leaving the page sends what is held. */
  const dismissTimers = useRef(new Map<string, { timer: number; signal: MarketSignal }>());
  const actionsRef = useRef(actions);
  useEffect(() => {
    actionsRef.current = actions;
  });

  const unhideDismissal = (id: string) =>
    setHeldDismissals((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const sendDismissal = useCallback((id: string) => {
    const held = dismissTimers.current.get(id);
    if (!held) return;
    window.clearTimeout(held.timer);
    dismissTimers.current.delete(id);
    actionsRef.current.dismissActive(held.signal, { onError: () => unhideDismissal(id) });
  }, []);

  useEffect(() => {
    const timers = dismissTimers.current;
    return () => {
      for (const id of [...timers.keys()]) sendDismissal(id);
    };
  }, [sendDismissal]);

  const holdDismissal = (signal: MarketSignal) => {
    setHeldDismissals((prev) => new Set(prev).add(signal.id));
    const timer = window.setTimeout(() => sendDismissal(signal.id), DISMISS_HOLD_MS);
    dismissTimers.current.set(signal.id, { timer, signal });
    queue.add(
      {
        title: "Dismissed · filed in History",
        description: `“${signal.title}” stops steering.`,
        countdown: Math.round(DISMISS_HOLD_MS / 1000),
        action: {
          label: "Undo",
          onPress: () => {
            const held = dismissTimers.current.get(signal.id);
            if (!held) return;
            window.clearTimeout(held.timer);
            dismissTimers.current.delete(signal.id);
            unhideDismissal(signal.id);
          },
        },
      },
      { timeout: DISMISS_HOLD_MS },
    );
  };

  const openSources = (signal: MarketSignal) => setSourcesFor({ signal });

  const showSource = (signal: MarketSignal, index: number) => {
    setSourceSheet({ signal, index });
    const id = signal.groundingObservations[index]?.id;
    if (id) setOpenedSources((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  };

  /* Signals and reports are linked by run: the report a signal was filed from. */
  const reportByRun = useMemo(() => {
    const map = new Map<string, FieldReport>();
    for (const r of reports) if (r.runId && !map.has(r.runId)) map.set(r.runId, r);
    return map;
  }, [reports]);
  const reportFor = useCallback(
    (signal: MarketSignal) =>
      signal.sourceRunId ? (reportByRun.get(signal.sourceRunId) ?? null) : null,
    [reportByRun],
  );

  /** Open Reports on the report a signal came from, at its filed entry. */
  const openReportFor = (signal: MarketSignal) => {
    const report = reportFor(signal);
    if (!report) return;
    setHistoryFocus(null);
    reportsFocusSeq.current += 1;
    setReportsFocus({ reportId: report.id, signalId: signal.id, seq: reportsFocusSeq.current });
    setActiveTab("reports");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el =
          document.getElementById(`sig-filed-${signal.id}`) ??
          document.getElementById("sig-brief-top");
        if (!el) return;
        el.focus({ preventScroll: true });
        el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
      });
    });
  };

  const busyId = actions.isBusy;

  const markRunwaysDrawn = useCallback(() => setRunwaysDrawn(true), []);

  /* ── Tabs ─────────────────────────────────────────────────────────────── */

  const switchTab = (key: SignalsTab) => {
    setHistoryFocus(null);
    setReportsFocus(null);
    setActiveTab(key);
  };

  // Roving-tabindex keyboard nav for the tablist.
  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, key: SignalsTab) => {
    const idx = TAB_ORDER.indexOf(key);
    let next = idx;
    switch (e.key) {
      case "ArrowRight":
        next = (idx + 1) % TAB_ORDER.length;
        break;
      case "ArrowLeft":
        next = (idx - 1 + TAB_ORDER.length) % TAB_ORDER.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = TAB_ORDER.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextKey = TAB_ORDER[next];
    switchTab(nextKey);
    const el = document.getElementById(`sig-tab-${nextKey}`);
    el?.focus();
    // The tablist scrolls on a narrow bar; the arrows are how a keyboard reaches its far end.
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  /** A filed signal in a report: go to the tab it lives under and bring it into view. */
  const openSignal = (signal: MarketSignal) => {
    const tab: SignalsTab =
      signal.status === "pending" ? "waiting" : signal.status === "active" ? "steering" : "history";
    setHistoryFocus(tab === "history" ? { id: signal.id, seq: Date.now() } : null);
    setReportsFocus(null);
    setActiveTab(tab);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`sig-${signal.id}`);
        if (!el) return;
        (el.querySelector<HTMLElement>("article") ?? el).focus({ preventScroll: true });
        el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
      });
    });
  };

  const unread = isLoading || isError;
  const counts: Record<SignalsTab, number | null> = {
    waiting: unread ? null : visiblePending.length,
    steering: unread ? null : active.length,
    reports: unread || reportsLoading ? null : reports.length,
    history: unread ? null : history.length,
  };

  const failedState = (
    <ErrorState
      align="start"
      className="sig-state sig-state--error"
      title="The signals did not load"
      body="Mohtawa could not read this brand's signals, so nothing is shown. An empty queue here would be a guess. Nothing you decided has changed."
      // Signals / The signals did not load: Try again is the primary capsule, the icon after it.
      actions={
        onRetry ? (
          <KitButton onClick={onRetry} iconRight={RotateCw} className="h-10 sig-state__wide">
            Try again
          </KitButton>
        ) : null
      }
    />
  );

  const panelMotion = reduced
    ? { initial: false as const }
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: HOUSE_TRANSITION,
      };

  const panelProps = (key: SignalsTab, busy: boolean) => ({
    role: "tabpanel" as const,
    id: `sig-panel-${key}`,
    "aria-labelledby": `sig-tab-${key}`,
    "aria-busy": busy || undefined,
    tabIndex: 0,
    className: "sig-panel",
    ...panelMotion,
  });

  const exitFor = (dir: "accept" | "decline" | undefined) =>
    reduced
      ? { opacity: 0, transition: { duration: 0 } }
      : {
          opacity: 0,
          x: dir === "accept" ? EXIT_DISTANCE : dir === "decline" ? -EXIT_DISTANCE : 0,
          transition: { duration: 0.32, ease: HOUSE_EASE },
        };

  return (
    <div className={cn("sig", isPhone && activeTab === "waiting" && "sig--deck")}>
      {/* The phone's top lane (library Bar / Top lane · Phone) stands in for the
          view header: the menu, the page's name in its own capsule, then New
          signal and M7 Agents in one capsule. The shell's floating menu steps
          aside while it is up (signals.css). */}
      {isPhone ? (
        <header className="sig-lane">
          <button
            type="button"
            className="sig-lane__btn sig-lane__glass"
            aria-label="Open navigation"
            disabled={!cockpit}
            onClick={(event) => cockpit?.openNav(event.currentTarget)}
          >
            <Menu aria-hidden="true" />
          </button>
          <h1 className="sig-lane__title sig-lane__glass">Signals</h1>
          <div className="sig-lane__end sig-lane__glass">
            {canEdit ? (
              <button
                type="button"
                className="sig-lane__btn"
                aria-label="New signal"
                onClick={() => setShowCreate(true)}
              >
                <Plus aria-hidden="true" />
              </button>
            ) : null}
            <span ref={laneSlotRef} className="sig-lane__slot" />
          </div>
        </header>
      ) : (
      <WallViewHeader
        title="Signals"
        collapse
        subhead={
          <StatusLine
            loading={isLoading}
            failed={isError}
            waiting={visiblePending.length}
            steering={active.length}
            nextDays={nextWindowDays}
          />
        }
      >
        {canEdit ? (
          <WallSaveButton onClick={() => setShowCreate(true)}>New signal</WallSaveButton>
        ) : null}
      </WallViewHeader>
      )}

      <WallBar
        axes={
          <div className="sig-tabs" role="tablist" aria-label="Signals sections" data-indicator>
            {TAB_ORDER.map((key) => {
              const on = activeTab === key;
              const count = counts[key];
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  id={`sig-tab-${key}`}
                  aria-selected={on}
                  aria-controls={`sig-panel-${key}`}
                  tabIndex={on ? 0 : -1}
                  className="sig-tab"
                  onClick={() => switchTab(key)}
                  onKeyDown={(e) => onTabKeyDown(e, key)}
                >
                  <span className="sig-tab__label">{TAB_LABELS[key]}</span>
                  {count != null ? <span className="sig-tab__count">{count}</span> : null}
                </button>
              );
            })}
            <KitTabIndicator selectedKey={activeTab} className="bottom-0" />
          </div>
        }
        readout={
          !unread && lastAccepted ? (
            <span className="wb-read sig-readout">
              Last accepted by <span className="wb-read-fig">{lastAccepted.name}</span> ·{" "}
              {formatShortDate(lastAccepted.at)}
            </span>
          ) : null
        }
      />

      <div className="sig-panels">
        {/* ============ WAITING ============ */}
        {activeTab === "waiting" && isPhone && (
          <motion.section {...panelProps("waiting", isLoading)}>
            {isError ? (
              failedState
            ) : isLoading ? (
              <DeckSkeleton />
            ) : (
              <TriageDeck
                pending={visiblePending}
                steering={active}
                hasReports={reports.length > 0}
                viewerName={viewerName}
                canEdit={canEdit}
                isBusy={busyId}
                reduced={reduced}
                onHold={holdDecision}
                onUndo={(s) => abortDeparting(s.id)}
                onSend={sendDecision}
                failed={deckFailure}
                onClearFailed={() => setDeckFailure(null)}
                onEdit={(s, patch) => actions.editPending(s, patch)}
                onOpenSource={showSource}
                reportFor={reportFor}
                onOpenReport={openReportFor}
                sheetOpen={sourceSheet !== null}
                openedSourceIds={openedSources}
                onSwitchTab={switchTab}
              />
            )}
          </motion.section>
        )}

        {activeTab === "waiting" && !isPhone && (
          <motion.section {...panelProps("waiting", isLoading)}>
            {/* Nothing waiting has no helper: the empty state says it all (Pen). */}
            {isError || isLoading || visiblePending.length === 0 ? null : (
              <p className="sig-helper">
                {canEdit
                  ? "Highest confidence first. Accepting puts a signal to work for its window; declining files it in History."
                  : "Highest confidence first. You can read every signal and its evidence; accepting or declining takes an editor."}
              </p>
            )}

            {isError ? (
              failedState
            ) : isLoading ? (
              <WaitingSkeleton />
            ) : (
              <>
                <div className="sig-queue" ref={queueRef}>
                  <AnimatePresence mode="popLayout" initial={false}>
                    {visiblePending.map((s) => {
                      const dir = departingDirs.get(s.id);
                      return (
                        <motion.div
                          key={s.id}
                          id={`sig-${s.id}`}
                          className="sig-queue__item"
                          data-sig-item=""
                          data-leaving={dir ? "true" : undefined}
                          layout={reduced ? false : "position"}
                          initial={false}
                          exit={exitFor(dir)}
                          transition={{ layout: HOUSE_TRANSITION }}
                        >
                          <PendingSignalCard
                            signal={s}
                            busy={busyId(s.id)}
                            settling={!!dir}
                            onOpenSources={(focusId) => setSourcesFor({ signal: s, focusId })}
                            report={reportFor(s)}
                            onOpenReport={() => openReportFor(s)}
                            onAccept={
                              canEdit
                                ? ({ validUntil, permanent }) =>
                                    exitPending(s, "accept", () =>
                                      actions.accept(s, validUntil, {
                                        onError: () => abortDeparting(s.id),
                                        onSuccess: () => {
                                          releaseDeparted(s.id);
                                          // Permanent: accept, then graduate into Brand DNA.
                                          if (permanent) actions.graduate(s);
                                        },
                                      }),
                                    )
                                : undefined
                            }
                            onDecline={
                              canEdit
                                ? () =>
                                    exitPending(s, "decline", () =>
                                      actions.decline(s, {
                                        onError: () => abortDeparting(s.id),
                                        onSuccess: () => releaseDeparted(s.id),
                                      }),
                                    )
                                : undefined
                            }
                            onEdit={canEdit ? (patch) => actions.editPending(s, patch) : undefined}
                          />
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
                {visiblePending.length === 0 ? (
                  <EmptyState
                    align="start"
                    className="sig-state"
                    icon={<Inbox aria-hidden />}
                    title="Nothing waiting"
                    body={
                      active.length > 0
                        ? `New reads appear here as the observatory finds them. ${countWord(active.length)} ${active.length === 1 ? "signal is" : "signals are"} steering the work in the meantime.`
                        : "New reads appear here as the observatory finds them."
                    }
                    actions={
                      active.length > 0 || reports.length > 0 ? (
                        <>
                          {active.length > 0 ? (
                            <StateAction onClick={() => switchTab("steering")}>
                              Open steering
                            </StateAction>
                          ) : null}
                          {reports.length > 0 ? (
                            <StateAction quiet onClick={() => switchTab("reports")}>
                              Read report
                            </StateAction>
                          ) : null}
                        </>
                      ) : undefined
                    }
                  />
                ) : null}
              </>
            )}
          </motion.section>
        )}

        {/* ============ STEERING ============ */}
        {activeTab === "steering" && (
          <motion.section {...panelProps("steering", isLoading)}>
            {isError || isLoading || active.length === 0 ? null : (
              <p className="sig-helper">
                Ending soonest first. Extend a window, graduate a signal into permanent Brand DNA,
                or let it run out.
              </p>
            )}

            {isError ? (
              failedState
            ) : isLoading ? (
              <SteeringSkeleton />
            ) : active.length > 0 ? (
              <SteeringSheet
                signals={active}
                canEdit={canEdit}
                isBusy={busyId}
                drawIn={!runwaysDrawn && !reduced}
                onDrawn={markRunwaysDrawn}
                onExtend={(s, validUntil) => actions.extend(s, validUntil)}
                onDismiss={holdDismissal}
                onGraduate={(s) => actions.graduate(s)}
                onOpenSources={openSources}
              />
            ) : (
              <EmptyState
                align="start"
                className="sig-state"
                icon={<Route aria-hidden />}
                title="No signals are steering yet"
                body="Accept a waiting signal to put it in motion. It steers the work for the window you give it, then ends or graduates into Brand DNA."
                actions={
                  visiblePending.length > 0 ? (
                    <StateAction primary className="sig-state__wide" onClick={() => switchTab("waiting")}>
                      See waiting
                    </StateAction>
                  ) : undefined
                }
              />
            )}
          </motion.section>
        )}

        {/* ============ REPORTS ============ */}
        {activeTab === "reports" && (
          <motion.section {...panelProps("reports", reportsLoading)}>
            {reportsLoading ? (
              <ReportsSkeleton />
            ) : reports.length > 0 ? (
              <>
                {/* Only when a waiting signal's report link brought the reader here. */}
                {reportsFocus ? (
                  <button type="button" className="sig-back" onClick={() => switchTab("waiting")}>
                    <ArrowLeft aria-hidden="true" />
                    Back to Waiting
                  </button>
                ) : null}
                <ReportsTrail
                  key={reportsFocus?.seq ?? 0}
                  reports={reports}
                  signals={signals}
                  onOpenSignal={openSignal}
                  initialReportId={reportsFocus?.reportId ?? null}
                  flashSignalId={reportsFocus?.signalId ?? null}
                  reduced={reduced}
                />
              </>
            ) : (
              <EmptyState
                align="start"
                className="sig-state"
                icon={<FileText aria-hidden />}
                title="No reports filed yet"
                body="The observatory files one each time it reads the market. The signals a report proposes arrive in Waiting."
              />
            )}
          </motion.section>
        )}

        {/* ============ HISTORY ============ */}
        {activeTab === "history" && (
          <motion.section {...panelProps("history", isLoading)}>
            {isError ? (
              failedState
            ) : isLoading ? (
              <HistorySkeleton />
            ) : history.length > 0 ? (
              <HistoryTrail
                key={historyFocus?.seq ?? 0}
                signals={history}
                allSignals={signals}
                memberNames={memberNames}
                canEdit={canEdit}
                isBusy={busyId}
                onGraduate={(s) => actions.graduate(s)}
                onOpenSources={openSources}
                initialFocusId={historyFocus?.id ?? null}
              />
            ) : (
              <EmptyState
                align="start"
                className="sig-state"
                icon={<HistoryIcon aria-hidden />}
                title="No resolved signals yet"
                body="A signal is filed here with its date when it is declined, dismissed, graduated into Brand DNA or runs out its window."
              />
            )}
          </motion.section>
        )}
      </div>

      {/* ============ DIALOGS (mounted only while open) ============ */}
      {showCreate && canEdit ? (
        <SignalsDialog
          labelledBy="sig-new-title"
          onClose={() => setShowCreate(false)}
          className="sig-dialog__panel--new"
        >
          <CreateSignalForm
            titleId="sig-new-title"
            onCreate={(draft) => {
              actions.create(draft);
              setShowCreate(false);
            }}
            onAskM7={(text) => {
              setShowCreate(false);
              agentRail.send(
                `Log a waiting signal from this, with its kind, headline, read and window, so I can check it in Waiting:\n\n${text}`,
              );
              queue.add({
                title: "M7 is writing the signal",
                description: "It lands in Waiting for you to check, then accept or decline.",
              });
            }}
            onCancel={() => setShowCreate(false)}
          />
        </SignalsDialog>
      ) : null}

      {sourcesFor ? (
        <SignalSourcesModal
          key={`${sourcesFor.signal.id}:${sourcesFor.focusId ?? ""}`}
          signal={sourcesFor.signal}
          initialId={sourcesFor.focusId}
          onClose={() => setSourcesFor(null)}
          onPickSource={
            isPhone
              ? (index) => {
                  setSourcesFor(null);
                  showSource(sourcesFor.signal, index);
                }
              : undefined
          }
        />
      ) : null}

      <AnimatePresence>
        {sourceSheet ? (
          <SourceSheet
            key="source-sheet"
            signal={sourceSheet.signal}
            index={sourceSheet.index}
            reduced={reduced}
            onIndexChange={(index) => showSource(sourceSheet.signal, index)}
            onClose={() => setSourceSheet(null)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
